import React, {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { Settings } from "../../../settings";
import FreeSpinOverlaySymbol from "../FreeSpinOverlaySymbol/FreeSpinOverlaySymbol";
import FreeSpinOverlayReelController from "./FreeSpinOverlayReelController";
import { safeInit } from "../../../utilities";

const FreeSpinOverlayReel = forwardRef(
  ({ index, symbolWidth, symbolHeight, reelSpacing, freeSpinOverlaySymbols }, ref) => {
    const overlayReelControllerRef = useRef(null);
    const overlaySymbolRefs = useRef(new Array(Settings.SYMBOLS_VISIBLE));
    const reelContainerRef = useRef(null);

    // Memoize container X position
    const containerX = useMemo(
      () => reelSpacing * (index - 2),
      [reelSpacing, index]
    );

    // Initialize controller once when component mounts
    useEffect(() => {
      if (!overlayReelControllerRef.current && reelContainerRef.current) {
        safeInit(() => {
          overlayReelControllerRef.current = new FreeSpinOverlayReelController(
            overlaySymbolRefs.current,
            index,
            symbolWidth,
            symbolHeight,
            reelContainerRef.current
          );
        });
      }

      return () => {
        if (overlayReelControllerRef.current) {
          overlayReelControllerRef.current.cleanup();
          overlayReelControllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle resize of all overlay symbols
    useEffect(() => {
      if (overlayReelControllerRef.current) {
        overlayReelControllerRef.current.resizeAnimations(
          symbolWidth,
          symbolHeight
        );
      }
    }, [symbolHeight, symbolWidth]);

    // Expose all imperative overlay reel controls
    useImperativeHandle(ref, () => ({
      getFreeSpinOverlayReelController: () =>
        overlayReelControllerRef.current,

      showWinAnimation: (symbolIndex, symbolId, animationType = "win") => {
        overlayReelControllerRef.current?.showWinAnimation(
          symbolIndex,
          symbolId,
          animationType
        );
      },

      showLandingAnimation: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showLandingAnimation(
          symbolIndex,
          symbolId
        );
      },

      showIdleAnimation: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.showIdleAnimation(
          symbolIndex,
          symbolId
        );
      },

      hideWinAnimation: (symbolIndex) => {
        overlayReelControllerRef.current?.hideWinAnimation(symbolIndex);
      },

      hideAllAnimations: () => {
        overlayReelControllerRef.current?.hideAllAnimations();
      },

      hideAllAnimationsExcept: (upcomingPositions) => {
        overlayReelControllerRef.current?.hideAllAnimationsExcept(
          upcomingPositions
        );
      },

      // Handle individual symbol reveal for landing animations
      handleSymbolReveal: (symbolIndex, symbolId) => {
        overlayReelControllerRef.current?.handleSymbolReveal(
          symbolIndex,
          symbolId
        );
      },
    }));

    return (
      <Container
        ref={reelContainerRef}
        name={`free_spin_spine_overlay_reel_${index}`}
        x={containerX}
      >
        {freeSpinOverlaySymbols.map((_, idx) => (
          <FreeSpinOverlaySymbol
            key={idx}
            index={idx}
            y={symbolHeight * idx}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            ref={(el) => {
              overlaySymbolRefs.current[idx] = el;
            }}
          />
        ))}
      </Container>
    );
  }
);

export default FreeSpinOverlayReel;
